<template>
	<v-card elevation="3">
		<v-form>
			<v-card-title>
				{{ $T('title') }}
				<v-spacer></v-spacer>
				<v-btn
					@click="$emit('close'); clear();"
					icon>
					<v-icon color="alert-color">mdi-close</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text>
				<v-row>
					<v-col cols="12">
						<v-autocomplete
							:disabled="!oneEmployee || !employees.length"
							v-model="employee"
							:items="employees"
							:loading="!employees.length"
							:error="invalid.includes(invalidEnum.name)"
							multiple
							return-object
							item-value="employeeId"
							:label="$T('employee')"
							:filter="
								(item, queryText) =>
									item.code?.includes(queryText) || item.arabicFullName.includes(queryText)
							"
							dense
							outlined
							hide-details
							@change="getStartEndDate"
						>
							<template v-slot:item="{ item }">
								<div
									class="d-flex justify-space-between text-caption"
									style="width: 100%">
									<div>
										({{ item.code }})
										{{ item.arabicFullName }}
									</div>
									<div>
										{{ item.sectionName }}
									</div>
								</div>
							</template>
							<template v-slot:selection="{ item, index }">
								<div
									v-if="index < 20"
									class="d-flex justify-space-between"
									style="min-width: 100%">
									<div>
										({{ item.code }})
										{{ item.arabicFullName }}
									</div>
									<div>
										{{ item.sectionName }}
									</div>
								</div>
								<span
									v-if="index === 20"
									class="text-caption text-start"
									style="width: 100%">
									+{{ employee?.length - 20 }}
									موظف اخرون
								</span>
							</template>
						</v-autocomplete>
					</v-col>
					<v-col cols="12">
						<bee-date-picker
							v-model="addWork.date"
							:input-label="$T('inputs.date')"
							:input-props="{
								outlined: true,
								dense: true,
								prependInnerIcon: '$calendar',
								error: invalid.includes(invalidEnum.date),
							}"
							menu-picker
							calendar
							no-title
							hide-details></bee-date-picker>
					</v-col>
					<v-col cols="6">
						<v-text-field
							v-model="addWork.startTime"
							type="time"
							:label="$T('enterTime')"
							:error="invalid.includes(invalidEnum.startTime)"
							:loading="loadingStartEndTime"
							outlined
							dense
							hide-details
						></v-text-field>
					</v-col>
					<v-col cols="6">
						<v-text-field
							v-model="addWork.endTime"
							type="time"
							:label="$T('leaveTime')"
							:error="invalid.includes(invalidEnum.endTime)"
							:min="addWork.startTime"
							:loading="loadingStartEndTime"
							outlined
							dense
							hide-details
						></v-text-field>
					</v-col>
					<v-col cols="12" class="py-0">
						<v-checkbox
							v-model="isFullDay"
							:label="$T('take-full-day-attendance')"
							:disabled="!Boolean(employee && employee.length > 0)"
							hide-details
							dense
							class="mt-0"
							@change="
								addWork.startTime = null;
								addWork.endTime = null;
								getStartEndDate();
							"
						/>
					</v-col>
				</v-row>
			</v-card-text>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn
					:loading="loadingAdd"
					:disabled="loadingAdd || cantAdd"
					color="secondary"
					@click="addDay">
					{{ $T('default-buttons.save') }}
				</v-btn>
			</v-card-actions>
		</v-form>
	</v-card>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

const invalidEnum = {
	name: 0,
	date: 1,
	startTime: 2,
	endTime: 3,
};

export default {
	name: 'AddWorkDayComponent',

	props: {
		oneEmployee: { 
			type: String,
			default: 'test',
		},
	},

	data() {
		return {
			loadingAdd: false,
			loadingStartEndTime: false,

			employee: [],
			addWork: {
				date: new Date().toISOString().slice(0, 10),
				startTime: null,
				endTime: null,
			},
			isFullDay: false,

			invalid: [],

			invalidEnum,
		};
	},

	computed: {
		cantAdd() {
			return (
				!this.employee?.length ||
				!this.addWork.startTime ||
				!this.addWork.endTime ||
				!this.addWork.date
			);
		},
		...mapState({
			employees: (state) => state.employee.employees,
		}),
	},

	methods: {
		addDay() {
			this.invalid = [];
			if (!this.employee) this.invalid.push(invalidEnum.name);
			if (!this.addWork.date) this.invalid.push(invalidEnum.date);
			if (!this.addWork.startTime) this.invalid.push(invalidEnum.startTime);
			if (!this.addWork.endTime) this.invalid.push(invalidEnum.endTime);

			if (this.invalid.length) return;
			console.log(this.employee.map((e) => e?.employeeId).filter(c => c !== null))
			this.loadingAdd = true;
			this.$store
				.dispatch('attendance/addAttendanceDay', {
					employeeIds: this.employee.map((e) => e?.employeeId).filter(c => c !== null && c !== undefined),
					arrivalTime: this.addWork.startTime,
					departureTime: this.addWork.endTime,
					date: this.addWork.date,
				})
				.then(() => {
					this.$emit('close');
					this.$emit('done');
					this.clear();
					this.$eventBus.$emit('show-snackbar', this.$T('messages.add-done'), {
						done: true,
					});
				})
				.catch((err) => {
					err.default && err.default(this.$T('messages.add-fail'), false);
				})
				.finally(() => {
					this.loadingAdd = false;
				});
		},

		clear() {
			this.employee = [];
			this.addWork = {
				date: new Date().toISOString().slice(0, 10),
				startTime: null,
				endTime: null,
			};
			this.isFullDay = false;
		},

		getStartEndDate() {
			if (this.isFullDay && this.employee.length) {
				this.loadingStartEndTime = true;
				Promise.all([
					this.$store.dispatch('employee/fetchEmployeeById', this.employee[0].employeeId),
					this.$store.dispatch('attendance/fetchAttendanceTypes')
				]).then(([employee, attendanceTypes]) => {
					const permanentSystem =
						attendanceTypes.find(c => c.id === employee.activePermanentSystem);

					if (!permanentSystem.dailySystems.length) return;
					
					const permanentDay =
						permanentSystem.dailySystems.find(c => c.day === moment(this.addWork.date).get('d'));
					this.addWork.startTime = moment(`${permanentDay.startDate}Z`).format('HH:mm');
					this.addWork.endTime = moment(`${permanentDay.endDate}Z`).format('HH:mm');
				}).finally(() => {
					this.loadingStartEndTime = false;
				})
			}
		}
	},

	mounted() {
		if (this.oneEmployee) this.employee = [this.employees.find((e) => e.employeeId === this.oneEmployee)];
	},

	/**
	 * 	2024-03-09T09:00:00Z
	 *  0001-01-01T09:00:00
	 *
	 */
};
</script>
